// src/pages/FrameIQShowcase.js
//
// Frame IQ "Showcase": a small library of pre-analysed videos for demos.
// The list comes from GET /video_intelligence/api/showcase (the latest
// successful analysis of each configured video, read from Redshift), so
// nothing here waits on a live analysis. Clicking a row opens the same
// report the analyzer shows, next to the video, with clickable timestamps.

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Toaster } from 'react-hot-toast'
import {
  Library,
  Loader2,
  ArrowLeft,
  Play,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
} from 'lucide-react'
import apiClient from '../utils/apiClient'
import { AnalysisResults } from './VideoIntelligenceAnalyze'
import savedShowcase from '../data/frameIqShowcase.json'

// Backup copy of the showcase results, bundled with the site. Used when the
// live endpoint (Redshift + backend) fails, times out, or returns nothing, so
// the page never shows an error or an empty table. Refresh this file after
// re-analysing a showcase video.
const SAVED_VIDEOS = savedShowcase.videos || []

// Longest we wait for the live endpoint before showing the saved copy.
const LIVE_TIMEOUT_MS = 10000

const SAFETY_BADGES = {
  safe: { cls: 'bg-green-100 text-green-800 border-green-300', icon: ShieldCheck, label: 'Safe' },
  caution: { cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: ShieldAlert, label: 'Caution' },
  unsafe: { cls: 'bg-red-100 text-red-800 border-red-300', icon: ShieldX, label: 'Unsafe' },
}

const AI_VERDICT_LABELS = {
  likely_authentic: 'Likely authentic',
  uncertain: 'Uncertain',
  possibly_ai_generated: 'Possibly AI-generated',
  likely_ai_generated: 'Likely AI-generated',
}

const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds)) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const pad = (n) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

const SafetyBadge = ({ value }) => {
  const badge = SAFETY_BADGES[value]
  if (!badge) return <span className="text-gray-400">—</span>
  const Icon = badge.icon
  return (
    <span
      className={`inline-flex items-center gap-1 border rounded-full px-2.5 py-0.5 text-sm font-medium ${badge.cls}`}
    >
      <Icon size={14} />
      {badge.label}
    </span>
  )
}

const FrameIQShowcase = () => {
  const [videos, setVideos] = useState(null) // null = not loaded yet
  const [loading, setLoading] = useState(true)
  const [usingSaved, setUsingSaved] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const playerRef = useRef(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/video_intelligence/api/showcase', {
        timeout: LIVE_TIMEOUT_MS,
      })
      const live = response.data?.videos || []
      if (live.length > 0) {
        setVideos(live)
        setUsingSaved(false)
      } else {
        setVideos(SAVED_VIDEOS)
        setUsingSaved(true)
      }
    } catch (err) {
      // Redshift/backend trouble: fall back quietly rather than showing an error.
      console.warn('Showcase: live data unavailable, showing saved copy.', err?.message)
      setVideos(SAVED_VIDEOS)
      setUsingSaved(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const selected = videos?.find((v) => v.video_id === selectedId) || null

  // Jump the embedded player to a moment (YouTube iframe API via postMessage;
  // works because the embed URL has enablejsapi=1).
  const seekTo = (seconds) => {
    const frame = playerRef.current
    if (!frame || !frame.contentWindow) return
    const send = (func, args) =>
      frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args }), '*')
    send('seekTo', [seconds, true])
    send('playVideo', [])
    // Only matters when the player isn't sticky (narrow screens, stacked layout).
    frame.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const header = (
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
        <Library className="text-gray-700" size={26} />
        Frame IQ Showcase
      </h1>
      <p className="text-gray-500 mt-1">
        Ready-made analyses of sample videos: brand safety, brand detection and content insights.
      </p>
    </div>
  )

  // ---------- Detail view ----------
  if (selected) {
    const backButton = (
      <button
        type="button"
        onClick={() => {
          setSelectedId(null)
          window.scrollTo({ top: 0 })
        }}
        className="inline-flex items-center gap-2 bg-white border border-gray-300 rounded px-3 py-1.5 text-gray-700 hover:bg-gray-100 font-medium shadow-sm"
      >
        <ArrowLeft size={16} />
        Back to showcase
      </button>
    )

    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <Toaster position="top-right" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5">
            {/* top-28 clears the app's fixed header so the player stays fully visible while the report scrolls */}
            <div className="lg:sticky lg:top-28">
              {/* Lives in the sticky column so it's always one click away, however far the report is scrolled */}
              <div className="mb-3">{backButton}</div>
              <div className="bg-white rounded shadow overflow-hidden">
                <div className="aspect-video bg-black">
                  <iframe
                    ref={playerRef}
                    title={selected.title}
                    src={`https://www.youtube.com/embed/${selected.video_id}?enablejsapi=1&rel=0&origin=${encodeURIComponent(
                      window.location.origin
                    )}`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-4">
                  <h2 className="text-lg font-semibold text-gray-800">{selected.title}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {[selected.channel, formatDuration(selected.duration_seconds)]
                      .filter((x) => x && x !== '—')
                      .join(' · ')}
                  </p>
                  <p className="text-xs text-gray-400 mt-3">
                    Click any timestamp in the report to jump to that moment.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <AnalysisResults analysis={selected.analysis} streaming={false} onSeek={seekTo} />
            <div className="pb-6">{backButton}</div>
          </div>
        </div>
      </div>
    )
  }

  // ---------- List view ----------
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Toaster position="top-right" />
      {header}

      {loading && (
        <div className="bg-white rounded shadow p-10 flex flex-col items-center text-center">
          <Loader2 size={32} className="animate-spin text-gray-400 mb-3" />
          <p className="text-gray-600">Loading showcase...</p>
        </div>
      )}

      {!loading && videos && videos.length === 0 && (
        <div className="bg-white rounded shadow p-10 text-center text-gray-600">
          No showcase videos are available yet.
        </div>
      )}

      {!loading && videos && videos.length > 0 && (
        <div className="bg-white rounded shadow overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Video</th>
                <th className="px-4 py-3 font-semibold">Brand safety</th>
                <th className="px-4 py-3 font-semibold">Brands detected</th>
                <th className="px-4 py-3 font-semibold">AI-generated?</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {videos.map((v) => {
                const a = v.analysis || {}
                const brands = a.brand_detection?.detected_brands || []
                const flagged = (a.segments || []).filter(
                  (seg) => seg.safety_flag && seg.safety_flag !== 'none'
                ).length
                return (
                  <tr
                    key={v.video_id}
                    onClick={() => setSelectedId(v.video_id)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-4">
                        <div className="relative flex-shrink-0">
                          <img
                            src={`https://i.ytimg.com/vi/${v.video_id}/mqdefault.jpg`}
                            alt=""
                            className="w-32 h-[72px] object-cover rounded bg-gray-200"
                            loading="lazy"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-800 line-clamp-2">{v.title}</p>
                          {v.channel && <p className="text-sm text-gray-500 mt-0.5">{v.channel}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <SafetyBadge value={a.overall_brand_safety} />
                      <p className="text-xs text-gray-500 mt-1">
                        {flagged > 0
                          ? `${flagged} flagged ${flagged === 1 ? 'moment' : 'moments'}`
                          : 'No flagged moments'}
                      </p>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="font-semibold text-gray-800">{brands.length}</p>
                      {brands.length > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {brands
                            .slice(0, 3)
                            .map((b) => b.brand_name)
                            .join(', ')}
                          {brands.length > 3 ? ` +${brands.length - 3} more` : ''}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {AI_VERDICT_LABELS[a.ai_content_assessment?.verdict] || '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 text-blue-600 font-medium whitespace-nowrap">
                        <Play size={14} />
                        View report
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && usingSaved && videos && videos.length > 0 && (
        <p className="text-xs text-gray-400 mt-3">Showing saved sample results.</p>
      )}
    </div>
  )
}

export default FrameIQShowcase
